/**
 * Authentication helpers for direct-endpoint mode.
 *
 * Direct-endpoint connections (`{ provider, endpoint, config }`) may carry an
 * API key so the STT WebSocket middleware can authenticate the browser.
 * Browsers cannot set request headers on a WebSocket handshake, so only two
 * transports are available:
 *
 * - `subprotocol` (default): the key travels as an extra
 *   `Sec-WebSocket-Protocol` token, `x-api-key.<key>`. The server strips the
 *   token before the application sees it and echoes back the FIRST requested
 *   subprotocol, so the integrator's own protocols always stay in front and
 *   the key token is always appended last. A key token sent as the only
 *   subprotocol is rejected by the server (`subprotocol_companion_required`),
 *   therefore the SDK falls back to `sofya-stt.v1` as the companion token when
 *   the integrator did not configure any protocol.
 * - `query`: the key travels as the `x-api-key` query parameter of the
 *   realtime URL. Less safe, because URLs are usually recorded by proxies,
 *   gateways and browser history.
 *
 * A JWT (`{ type: "jwt", token }`) is an alternative to the API key, never a
 * complement: it travels as the FIRST subprotocol on the WebSocket, so the
 * server echoes it back, and as `Authorization: Bearer <token>` on the HTTP
 * calls the SDK makes. The integrator passes the raw JWT; the SDK adds the
 * `Bearer ` prefix itself.
 */

/** Companion subprotocol used when the integrator configured no protocols. */
export const DEFAULT_STT_SUBPROTOCOL = "sofya-stt.v1";

/** Prefix of the subprotocol token that carries the API key. */
export const API_KEY_SUBPROTOCOL_PREFIX = "x-api-key.";

/** Query parameter and HTTP header name used for the API key. */
export const API_KEY_PARAM = "x-api-key";

/** Close code used by the server when it rejects the API key. */
export const AUTH_REJECTED_CLOSE_CODE = 4401;

/** Placeholder written instead of the API key in logs and debug output. */
export const REDACTED_VALUE = "[redacted]";

export type ApiKeyAuthTransport = "subprotocol" | "query";

export type NoAuth = { type: "none" };

export type ApiKeyAuth = {
  type: "api_key";
  key: string;
  /** Defaults to `"subprotocol"`. */
  transport?: ApiKeyAuthTransport;
};

export type JwtAuth = {
  type: "jwt";
  /**
   * The raw JWT. **Do not prefix it with `Bearer `**: the SDK adds the prefix
   * to the HTTP calls and throws `SofyaAuthError("INVALID_JWT")` if it is
   * already there.
   */
  token: string;
};

export type DirectModeAuth = NoAuth | ApiKeyAuth | JwtAuth;

export type SofyaAuthErrorCode =
  | "INVALID_API_KEY"
  | "INVALID_API_KEY_CHARACTERS"
  | "INVALID_JWT"
  | "INVALID_JWT_CHARACTERS"
  | "AUTH_REJECTED";

/** Stable SDK error for authentication configuration and handshake failures. */
export class SofyaAuthError extends Error {
  public readonly code: SofyaAuthErrorCode;
  public readonly status: number | null;

  constructor(
    code: SofyaAuthErrorCode,
    message: string,
    options: { status?: number | null } = {}
  ) {
    super(message);
    this.name = "SofyaAuthError";
    this.code = code;
    this.status = options.status ?? null;
  }
}

/**
 * RFC 6455 subprotocol tokens are HTTP tokens: `=`, `/`, `,`, `;`, spaces and
 * quotes are not allowed. The SDK never transforms the key, it only validates.
 */
const RFC6455_TOKEN_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

export const normalizeProtocols = (
  protocols?: string | string[]
): string[] => {
  if (!protocols) {
    return [];
  }

  const list = Array.isArray(protocols) ? protocols : [protocols];

  return list.filter(
    (protocol): protocol is string =>
      typeof protocol === "string" && protocol.length > 0
  );
};

export const resolveAuthTransport = (
  auth?: DirectModeAuth
): ApiKeyAuthTransport => {
  if (!auth || auth.type !== "api_key") {
    return "subprotocol";
  }

  return auth.transport ?? "subprotocol";
};

/** Returns the API key when the connection is configured with `api_key` auth. */
export const getApiKey = (auth?: DirectModeAuth): string | null => {
  if (!auth || auth.type !== "api_key") {
    return null;
  }

  return auth.key;
};

const BEARER_PREFIX_PATTERN = /^bearer\s/i;

/** Returns the JWT when the connection is configured with `jwt` auth. */
export const getJwt = (auth?: DirectModeAuth): string | null => {
  if (!auth || auth.type !== "jwt") {
    return null;
  }

  return auth.token;
};

const assertValidJwt = (auth: JwtAuth, mode?: string): void => {
  if (typeof auth.token !== "string" || auth.token.length === 0) {
    throw new SofyaAuthError(
      "INVALID_JWT",
      "auth.token must be a non-empty string when auth.type is \"jwt\"."
    );
  }

  if (BEARER_PREFIX_PATTERN.test(auth.token)) {
    throw new SofyaAuthError(
      "INVALID_JWT",
      'auth.token must be the raw JWT, without the "Bearer " prefix; the SDK adds it.'
    );
  }

  // Batch sessions send the JWT only as a header, so any character is fine.
  if (mode !== "batch" && !RFC6455_TOKEN_PATTERN.test(auth.token)) {
    throw new SofyaAuthError(
      "INVALID_JWT_CHARACTERS",
      "auth.token cannot be sent as a WebSocket subprotocol: RFC 6455 tokens " +
        "accept only letters, digits and !#$%&'*+-.^_`|~ (no \"=\", \"/\", " +
        "\",\", spaces or quotes), so the browser would refuse the handshake. " +
        "A standard JWT (base64url, no padding) is always accepted."
    );
  }
};

/**
 * Validates the configured authentication before any connection is opened.
 * Throws `SofyaAuthError` with a clear message when the credential cannot be
 * used. `mode` is the session mode (`"batch"` skips the subprotocol checks).
 */
export const assertValidAuthConfig = (
  auth?: DirectModeAuth,
  mode?: string
): void => {
  if (!auth || auth.type === "none") {
    return;
  }

  if (auth.type === "jwt") {
    assertValidJwt(auth, mode);
    return;
  }

  if (auth.type !== "api_key") {
    throw new SofyaAuthError(
      "INVALID_API_KEY",
      `Unsupported auth type: ${String((auth as { type?: unknown }).type)}.`
    );
  }

  if (typeof auth.key !== "string" || auth.key.length === 0) {
    throw new SofyaAuthError(
      "INVALID_API_KEY",
      "auth.key must be a non-empty string when auth.type is \"api_key\"."
    );
  }

  const transport = resolveAuthTransport(auth);

  if (transport !== "subprotocol" && transport !== "query") {
    throw new SofyaAuthError(
      "INVALID_API_KEY",
      `Unsupported auth.transport: ${String(transport)}. Use "subprotocol" or "query".`
    );
  }

  if (transport === "subprotocol" && !RFC6455_TOKEN_PATTERN.test(auth.key)) {
    throw new SofyaAuthError(
      "INVALID_API_KEY_CHARACTERS",
      "auth.key cannot be sent as a WebSocket subprotocol: RFC 6455 tokens " +
        "accept only letters, digits and !#$%&'*+-.^_`|~ (no \"=\", \"/\", " +
        "\",\", spaces or quotes). Issue a token-safe key or use " +
        'auth.transport: "query".'
    );
  }
};

/**
 * Builds the subprotocol list sent on the handshake.
 *
 * Without `api_key` auth the integrator's `protocols` are forwarded untouched
 * (including `undefined`), so existing integrations keep their exact behavior.
 */
export const resolveRealtimeProtocols = (
  protocols?: string | string[],
  auth?: DirectModeAuth
): string | string[] | undefined => {
  const jwt = getJwt(auth);

  // The JWT goes first so the server echoes it back.
  if (jwt !== null) {
    return [jwt, ...normalizeProtocols(protocols)];
  }

  const key = getApiKey(auth);

  if (key === null || resolveAuthTransport(auth) !== "subprotocol") {
    return protocols;
  }

  const configured = normalizeProtocols(protocols);
  const companions =
    configured.length > 0 ? configured : [DEFAULT_STT_SUBPROTOCOL];

  return [...companions, `${API_KEY_SUBPROTOCOL_PREFIX}${key}`];
};

/** Appends `x-api-key=<key>` to the realtime URL for the `query` transport. */
export const applyAuthToRealtimeUrl = (
  url: URL,
  auth?: DirectModeAuth
): URL => {
  const key = getApiKey(auth);

  if (key !== null && resolveAuthTransport(auth) === "query") {
    url.searchParams.set(API_KEY_PARAM, key);
  }

  return url;
};

/**
 * Headers the SDK adds to the HTTP calls it makes (batch reprocess, audit
 * ingestion). An `x-api-key` already configured by the integrator wins.
 */
export const resolveAuthHeaders = (
  headers?: Record<string, string>,
  auth?: DirectModeAuth
): Record<string, string> | undefined => {
  const key = getApiKey(auth);

  if (key === null) {
    return headers ? { ...headers } : undefined;
  }

  const hasApiKeyHeader = Object.keys(headers ?? {}).some(
    (header) => header.toLowerCase() === API_KEY_PARAM
  );

  if (hasApiKeyHeader) {
    return { ...headers };
  }

  return { [API_KEY_PARAM]: key, ...headers };
};

/**
 * Token the SDK turns into `Authorization: Bearer <token>` on its HTTP calls:
 * `auth.token` for `jwt` auth, otherwise the deprecated `config.token`.
 */
export const resolveBearerToken = (
  auth?: DirectModeAuth,
  legacyToken?: string
): string | undefined => getJwt(auth) ?? legacyToken;

/** Replaces the `x-api-key` query parameter value with a placeholder. */
export const redactUrl = (url: string): string => {
  if (typeof url !== "string" || !url.includes(API_KEY_PARAM)) {
    return url;
  }

  return url.replace(
    new RegExp(`([?&]${API_KEY_PARAM}=)[^&#]*`, "gi"),
    `$1${encodeURIComponent(REDACTED_VALUE)}`
  );
};

/** Replaces the API key inside subprotocol tokens with a placeholder. */
export const redactProtocols = (
  protocols?: string | string[]
): string | string[] | undefined => {
  const redactToken = (protocol: string) =>
    protocol.startsWith(API_KEY_SUBPROTOCOL_PREFIX)
      ? `${API_KEY_SUBPROTOCOL_PREFIX}${REDACTED_VALUE}`
      : protocol;

  if (typeof protocols === "string") {
    return redactToken(protocols);
  }

  if (Array.isArray(protocols)) {
    return protocols.map(redactToken);
  }

  return protocols;
};

/**
 * Shallow copy of a connection config safe to log: the API key, the key
 * subprotocol token and the `x-api-key` header/query value are redacted.
 */
export const redactAuthConfig = <T extends Record<string, any>>(
  config: T | null | undefined
): T | null | undefined => {
  if (!config || typeof config !== "object") {
    return config;
  }

  const redacted: Record<string, any> = { ...config };

  if (redacted.auth && typeof redacted.auth === "object") {
    redacted.auth =
      redacted.auth.type === "api_key"
        ? { ...redacted.auth, key: REDACTED_VALUE }
        : redacted.auth.type === "jwt"
          ? { ...redacted.auth, token: REDACTED_VALUE }
          : { ...redacted.auth };
  }

  if (redacted.protocols) {
    redacted.protocols = redactProtocols(redacted.protocols);
  }

  if (redacted.headers && typeof redacted.headers === "object") {
    redacted.headers = Object.fromEntries(
      Object.entries(redacted.headers as Record<string, string>).map(
        ([header, value]) => [
          header,
          header.toLowerCase() === API_KEY_PARAM ? REDACTED_VALUE : value,
        ]
      )
    );
  }

  if (typeof redacted.endpoint === "string") {
    redacted.endpoint = redactUrl(redacted.endpoint);
  } else if (
    redacted.endpoint &&
    typeof redacted.endpoint === "object" &&
    typeof redacted.endpoint.endpoint === "string"
  ) {
    redacted.endpoint = {
      ...redacted.endpoint,
      endpoint: redactUrl(redacted.endpoint.endpoint),
    };
  }

  return redacted as T;
};
